// redux/slices/surveyLinksSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl, getAuthHeaders } from '../../Utils/serverSelector';

const authConfig = async () => ({
    headers: await getAuthHeaders(),
    withCredentials: true,
    validateStatus: () => true,
});

const authJsonConfig = async () => {
    const base = await authConfig();
    return { ...base, headers: { ...base.headers, 'Content-Type': 'application/json' } };
};

/* ── fetchPublicSurvey (без авторизации) ── */
export const fetchPublicSurvey = createAsyncThunk(
    'surveyLinks/fetchPublic',
    async (token, { rejectWithValue }) => {
        try {
            const res = await axios.get(
                `${getServerUrl()}/public/survey/${token}`,
                { validateStatus: () => true }
            );
            if (res.status !== 200) throw new Error(res.data?.error || `HTTP ${res.status}`);
            return res.data;
        } catch (e) { return rejectWithValue(e?.response?.data?.error || e.message); }
    }
);

/* ── fetchSurveyLinks ── */
export const fetchSurveyLinks = createAsyncThunk(
    'surveyLinks/fetchAll',
    async (uid, { rejectWithValue }) => {
        try {
            const cfg = await authConfig();
            const res = await axios.get(`${getServerUrl()}/surveyLinks/master/${uid}`, cfg);
            if (res.status !== 200) throw new Error(res.data?.error || `HTTP ${res.status}`);
            return Array.isArray(res.data) ? res.data : [];
        } catch (e) { return rejectWithValue(e?.response?.data?.error || e.message); }
    }
);

/* ── createSurveyLink ── */
export const createSurveyLink = createAsyncThunk(
    'surveyLinks/create',
    async (payload, { rejectWithValue }) => {
        try {
            const cfg = await authJsonConfig();
            const res = await axios.post(`${getServerUrl()}/surveyLinks`, payload, cfg);
            if (res.status !== 200 && res.status !== 201) throw new Error(res.data?.error || `HTTP ${res.status}`);
            return res.data;
        } catch (e) { return rejectWithValue(e?.response?.data?.error || e.message); }
    }
);

/* ── deleteSurveyLink ── */
export const deleteSurveyLink = createAsyncThunk(
    'surveyLinks/delete',
    async (id, { rejectWithValue }) => {
        try {
            const cfg = await authConfig();
            const res = await axios.delete(`${getServerUrl()}/surveyLinks/${id}`, cfg);
            if (res.status !== 200) throw new Error(res.data?.error || `HTTP ${res.status}`);
            return id;
        } catch (e) { return rejectWithValue(e?.response?.data?.error || e.message); }
    }
);

/* ── uploadPdf ── */
export const uploadSurveyPdf = createAsyncThunk(
    'surveyLinks/uploadPdf',
    async ({ file, masterUid }, { rejectWithValue }) => {
        try {
            const headers = await getAuthHeaders();
            const fd = new FormData();
            fd.append('pdf', file);
            fd.append('masterUid', masterUid);
            const res = await axios.post(`${getServerUrl()}/surveyLinks/upload-pdf`, fd, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
                validateStatus: () => true,
            });
            if (res.status !== 200) throw new Error(res.data?.error || `HTTP ${res.status}`);
            return res.data; // { path, name, url }
        } catch (e) { return rejectWithValue(e?.response?.data?.error || e.message); }
    }
);

/* ── slice ── */
const surveyLinksSlice = createSlice({
    name: 'surveyLinks',
    initialState: {
        links: [],
        linksStatus: 'idle',
        currentSurvey: null,
        status: 'idle',
        error: null,
    },
    reducers: {
        clearCurrentSurvey: (s) => { s.currentSurvey = null; },
    },
    extraReducers: (b) => b
        // fetchPublic
        .addCase(fetchPublicSurvey.pending, s => { s.status = 'loading'; s.currentSurvey = null; })
        .addCase(fetchPublicSurvey.fulfilled, (s, a) => { s.currentSurvey = a.payload; s.status = 'succeeded'; })
        .addCase(fetchPublicSurvey.rejected, (s, a) => { s.status = 'failed'; s.error = a.payload; })
        // fetchAll
        .addCase(fetchSurveyLinks.pending, s => { s.linksStatus = 'loading'; s.error = null; })
        .addCase(fetchSurveyLinks.fulfilled, (s, a) => { s.links = a.payload; s.linksStatus = 'succeeded'; })
        .addCase(fetchSurveyLinks.rejected, (s, a) => { s.linksStatus = 'failed'; s.error = a.payload; })
        // create
        .addCase(createSurveyLink.fulfilled, (s, a) => { s.links.unshift(a.payload); })
        // delete
        .addCase(deleteSurveyLink.fulfilled, (s, a) => { s.links = s.links.filter(l => l.id !== a.payload); })
        // reset
        .addCase(resetStore, () => ({
            links: [], linksStatus: 'idle',
            currentSurvey: null, status: 'idle', error: null,
        })),
});

export const { clearCurrentSurvey } = surveyLinksSlice.actions;
export default surveyLinksSlice.reducer;